/**
  * @class meleList
  * @description
  *
  * # Purpose
  * Implements `<mele-list>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-list></mele-list>';
  * ```
  * Create element in JavaScript:
  * ```
  * const el = elParent.appendChildren({
  *   tag: 'mele-list',
  *   sort: 'label$+∫label2$+',
  *   searchbar: true,
  *   filter: 'label=un.*ed',
  *   body: [
  *     {key: 'item1', label: 'Item #1'},
  *     {key: 'item2', label: 'Item #2'},
  *   ]
  *   ... other attributes ...
  * })
  * el.add([
  *   {key: 'item3', label: 'Item #3'},
  *   {key: 'item4', label: 'Item #4', label2: 'Some label2'},
  * ])
  * ```
  *
  * # Implementation notes
  *
  */

import meleView from 'meleView'

const PROPERTIES = _.extend({}, {
  prompt: { type: 'string', description: 'prompt displayed in header of modal selector' },

  prefix: { type: 'string', description: 'sets the prefix for all items' },
  auto: { type: 'boolean', description: 'if set, will automatically populate items with trnaslation terms starting with value of prefix attribute' },
  blank: { type: 'boolean', description: 'if set, will allow empty item' },

  direction: { type: 'string', description: 'type determines', enum: ['column', 'row'], default: 'column' },
  design: { type: 'string', description: 'determines style of individual item', enum: ['none', 'group', 'oval', 'block', 'grid', 'tag', 'toolbar'] },

  multi: { type: 'boolean', description: 'if true, allows selection of multiple items' },
  preselected: { type: 'string', description: 'item preselected within dropdwon by navigation key' },
  selected: { type: 'array', description: 'list of keys of selected items' },
  collapse: { type: 'array', description: 'list of keys of collapsed items' },
  isJustIconCollapsible: { type: 'boolean', default: false, description: 'if true, the items will be collapsible by clicking on the icon, but the content will not be collapsed' },
  noncollapsible: { type: 'boolean', description: 'Disables collapse/expand of the items by user' },
  show: { type: 'string', description: 'determines which items to show', enum: ['all', 'selected', 'notselected'], default: 'all' },
  readonly: { type: 'boolean', description: 'Defines if the element acts as a getter or a setter' },
  editable: { type: 'boolean', description: 'if true, user can remove/add items in the list' },

  label: { type: 'string', description: 'Sets the template for constructing label of items; e.g.  prefix $ key' },
  label2: { type: 'string', description: 'Sets the template for constructing label2 of items; e.g. label2:$ prefix $ key' },
  value: { type: 'string', description: 'Sets the template for constructing displayed value of items; e.g. $ prefix $ key' },
  attachments: { type: 'string', description: 'Sets the template for constructing displayed attachments of items' },
  tooltip: { type: 'string', description: 'Sets the template for constructing tooltip of items; e.g. $ prefix $ key' },
  color: { type: 'string', description: 'Sets the template for constructing color of items; e.g. color:$ prefix $ key' },
  indenticon: { type: 'string', description: "set which icon should be used for indented items. Exactly two items are necessary for this to work properly, separated by ','" },
  iconleft: { type: 'string', description: 'Sets the template for constructing iconleft parameter of items; e.g. $ prefix $ key' },
  iconright: { type: 'string', description: 'Sets the template for constructing iconright of items; e.g. $ prefix $ key' },

  sort: { type: 'array', description: 'list of property+ or property- indicates sorting criteria; $ suffix after property name indicates sorting over translation of the property, @ suffix means numeric sorting' },

  searchbar: { type: 'boolean', description: 'whether to show search bar' },
  searchfields: { type: 'array', description: 'list of properties used for searching; if property is followed by $, search will be done over translation of the property value; if unspecified, defaults to label$∫label2$' },
  search: { type: 'string', description: 'search term; if set, list will display only items containing the term in at least one field defined in searchfields' },
  searching: { type: 'boolean', description: 'if true, displays search in progress indicator' },
  searchfocused: { type: 'boolean', description: 'indicates search field is focused' },

  filterbar: { type: 'boolean', description: 'whether to show filter bar' },
  filtersets: { type: 'array', description: 'list of [setname,filterdefinition] tuples defines preconfigured filtering sets' },
  filterset: { type: 'string', description: 'indicates selected filter set' },
  filter: { type: 'array', description: 'list of [property,regexp] tuples; causes filtering out non-matching items' },
  urlhash: { type: 'string', description: 'if set, change of selected table item will change window URL hash' },

  limit: { type: 'integer', description: 'limits number of items in the list; removes items above the threshold; for negative number, limit is applied from beginning of the list' },

  allselectable: { type: 'boolean', description: 'when true, all list items, including collapsible items, are selectable', default: false },

  sortable: { type: 'boolean', description: 'list will become sortable by dragging' },

  autoselectable: { type: 'boolean', description: 'whether to display button for auto select' },
  autoselect: { type: 'boolean', description: 'whether to aytomatically select added new item' },

  eventSorted: { type: 'function', description: 'method that can be overwritten to inform that element sorting has been changed' },
  eventSelected: { type: 'function', description: 'method that can be overwritten to inform about change of selected items; default will publish mmListSelected' },
  eventSearch: { type: 'function', description: 'method that can be overwritten to provide custom search results in dropdowns' },
}, meleView._properties)

export default class meleList extends meleView {
  constructor(self) {
    super(self)
    this._values.selected = []
    this._values.collapse = []
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()

    this._body.onclick = ()=>this.bodyClicked()
    this._dependencies.language.search = 1 // searchChanged will be always called when language changes

    // create comber
    this._comber = this.createElementFast('div')
    this._comber.setAttribute('key', ':comber')
    this._comber.contentEditable = true
    this._comber.setAttribute('type', 'string')
    this._comber.onclick = ()=>this.bodyClicked()
    this._comber.onkeypress = (event)=>{
      if(event.which !== 13) return true
      const el = event.target
      const newKey = this.addCustomOption(el.innerText)
      el.innerHTML = ''
      if(newKey) this.changeSelected(newKey, true)
      return false
    }
    this._comber.oninput = (event)=>{
      const el = event.target
      const body = el.ownerDocument.body
      const elDropdownList = body.find('>.mele-modal .mele-list')
      if(elDropdownList) elDropdownList.search = el.innerText
    }
    this._body.appendChild(this._comber)

    // edit button
    this._editbutton = this._body_wrapper.appendChildren({
      tag: 'mele-button',
      type: 'iconmini',
      onclick: ()=>{ this.editbutton(this._editbutton.key) },
    })

    // search
    this._search_wrapper = this.createElementFast('mele-header')
    this._search_wrapper.setAttribute('key', 'search')
    this._search_wrapper.setAttribute('hidden', '')

    const me = this
    this._search = this._search_wrapper.appendChildren({
      tag: 'mele-input',
      type: 'string',
      value: '',
      split: '0:1',
      blank: true,
      hidden: true,
      onkeydown: (e)=>{
        const keyCode = e.keyCode || e.which
        const filtered = me.items.filter(i => i.key && !i.hidden)
        const itemsLength = filtered.length
        if(itemsLength < 1) return
        if(keyCode === 13) { // (Enter key)
          if(itemsLength > 0) {
            const selector = this.preselected || filtered[0].key
            this.itemClicked(me.getItemElement(selector))
            // this.changeSelected(selector, true, true)
          }
        } else if(keyCode === 27) { // (Escape key)
          this.preselected = ''
          this.modalClose(':dropdown')
        } else if(keyCode === 40) { // (Down arrow key)
          let nextIdx = 0
          const idx = _.findIndex(filtered, i=>i.key===me.preselected)
          if(parseInt(idx) < (itemsLength-1)) nextIdx = idx+1
          me.preselected = filtered[nextIdx].key
        } else if(keyCode === 38) { // (Up arrow key)
          let nextIdx = filtered.length - 1
          const idx = _.findIndex(filtered, i=>i.key===me.preselected)
          if(parseInt(idx) > 0) nextIdx = idx-1
          me.preselected = filtered[nextIdx].key
        }
      },
      eventValueChanged: (el, value)=>{
        if(this.search !== value) this.search = value
        if(this._customoption) { this._customoption.disabled = value==='' || this.getItem(value) }
      },
      eventValueSubmitted: (el, value)=>{
        if(this.editable) {
          const newKey = this.addCustomOption(value)
          if(newKey) {
            if(newKey) this.changeSelected(newKey, true)
            if(!this.multi) this.modalClose(':dropdown')
          }
        }
      },
    })

    this._searching = this._search_wrapper.appendChildren({
      tag: 'mele-image',
      icon: 'sys.wait',
      hidden: true,
    })

    this._customoption = this._search_wrapper.appendChildren({
      tag: 'mele-button',
      type: 'toolbar',
      iconleft: 'sys.plus',
      disabled: true,
      eventButtonClicked: (el)=>{
        const newKey = this.addCustomOption(this._search.value)
        if(newKey) {
          if(newKey) this.changeSelected(newKey, true)
          if(!this.multi) this.modalClose(':dropdown')
        }
      },
    })

    this._header_wrapper.appendChild(this._search_wrapper)

    this.formStatusChanged()
    this._filterItems()
    this._sortItems()
    this._limitItems()
  } // init

  navigateThroughItems(idx, direction) {
    const preselected = _.findIndex(this.items, i=>i.key==this.preselected)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * ensure first selected item is visible within the scroll area
  */
  scrollToSelected() {
    const key = this.selected[0]
    const el = this._body.find('>@{'+_.toString(key)+'}')
    if(el && el.scrollIntoViewIfNeeded) {
      _.throttle(el.scrollIntoViewIfNeeded.bind(el), 200)()
    }
  } // scrollToSelected

  preselectedChanged(value) {
    this.getItemElements().forEach(el=>{
      if(el.key == value) {
        el.setAttribute('preselected', true)
      } else {
        el.removeAttribute('preselected')
      }
    })
    const el = this.getItemElement(value)
    if(el && el.scrollIntoViewIfNeeded) {
      _.throttle(el.scrollIntoViewIfNeeded.bind(el), 200)()
    }
  }

  sortableChanged(value) {
    if(value) {
      const sort = Array.from(this.sort)
      if(!sort.includes('sorting#+')) {
        sort.unshift('sorting#+')
        this.sort = sort
      }
      this._body.addEventListener('drop', this._setOnDropHandler.bind(this), true)
      this._body.addEventListener('dragover', this._setOnDragoverHandler.bind(this), true)

      this.getItemElements().forEach((el, idx)=>{
        el.addEventListener('dragstart', this._setOnDragstartHandler, true)
        el.setAttribute('draggable', true)
        el.sorting = parseInt(el.data.sorting) || (idx+1) * 1000
      })

      this._sortItems()
    } else {
      this._body.removeEventListener('drop', this._setOnDropHandler)
      this._body.removeEventListener('dragover', this._setOnDragoverHandler)
      this.getItemElements().forEach(el=>{
        el.removeEventListener('dragstart', this._setOnDragstartHandler)
        el.removeAttribute('draggable')
      })
    }
  }

  _setOnDropHandler(e) {
    e.preventDefault()
    const target = e.target.closest('.mele-item')
    this._body.forAll('mele-item.dragover', el=>el.classList.remove('dragover'))
    const prev = target.previousSibling || 0
    const key = e.dataTransfer.getData('text')
    const original = this.getItemElement(key)
    if(target.sorting == original.sorting) return
    const sorting = parseInt((parseInt(target.sorting) + parseInt(prev.sorting)) / 2)
    original.sorting = sorting
    this.eventSorted(this, original, sorting)
    this._sortItems()
    target.scrollIntoView({ block: 'center' })
  }

  _setOnDragoverHandler(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const target = e.target.closest('.mele-item')
    const key = e.dataTransfer.getData('text')
    const original = this.getItemElement(key)
    if(target && target != original && !target.classList.contains('dragover')) {
      this._body.forAll('mele-item.dragover', el=>el.classList.remove('dragover'))
      target.classList.add('dragover')
    }
  }

  _setOnDragstartHandler(e) {
    e.dataTransfer.setData('text/plain', e.target.key)
    e.dataTransfer.dropEffect = 'move'
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * selects first item
  */
  selectFirst() {
    const first = this._body.find('>:not([key=":comber"])')
    if(first) this.selected = [first.key]
  } // selectFirst

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * selects last item
  */
  selectLast() {
    const all = this._body.findAll('>:not([key=":comber"])')
    if(all.length===0) return
    const elLast = all[all.length-1]
    const currentKey = this.selected[0]
    if(elLast && _.toString(elLast.key)!='' && elLast.key!==currentKey) {
      this.selected = [elLast.key]
    }
  } // selectLast

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * selects next item; at the end of items, will go to first item if circular is set, or do nothing
  */
  selectNext(circular, steps=1) {
    const elFirst = this._body.find('>:not([key=":comber"])')
    if(!elFirst) return
    const currentKey = this.selected[0]
    let elCurrent = currentKey ? this._body.find('>:not([key=":comber"])@{'+currentKey+'}') : null
    if(!elCurrent) {
      if(elFirst && _.toString(elFirst.key)!='') { this.selected = [elFirst.key] }
      return
    }
    for(let i=1; i<=steps; i++) {
      const elNext = elCurrent.nextElementSibling
      if(!elNext || _.toString(elNext.key)=='') {
        if(circular) {
          if(elCurrent && _.toString(elCurrent.key)!='' && elCurrent.key!==currentKey) {
            this.selected = [elCurrent.key]
          }else {
            this.selected = [elFirst.key]
          }
          return
        }else{
          if(elCurrent && _.toString(elCurrent.key)!='' && elCurrent.key!==currentKey) {
            this.selected = [elCurrent.key]
          }
          return
        }
      }
      elCurrent = elNext
    }
    this.selected = [elCurrent.key]
  } // selectNext

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * selects previous item; at the beginning of items, will go to last item if circular is set, or do nothing
  */
  selectPrevious(circular, steps=1) {
    const elLast = this._body.lastChild
    if(!elLast) return
    const currentKey = this.selected[0]
    let elCurrent = currentKey ? this._body.find('>:not([key=":comber"])@{'+currentKey+'}') : null
    if(!elCurrent) {
      if(elLast && _.toString(elLast.key)!='') { this.selected = [elLast.key] }
      return
    }
    for(let i=1; i<=steps; i++) {
      const elPrev = elCurrent.previousElementSibling
      if(!elPrev || _.toString(elPrev.key)=='') {
        if(circular) {
          if(elCurrent && _.toString(elCurrent.key)!='' && elCurrent.key!==currentKey) {
            this.selected = [elCurrent.key]
          }else {
            this.selected = [elLast.key]
          }
          return
        }else{
          if(elCurrent && _.toString(elCurrent.key)!='' && elCurrent.key!==currentKey) {
            this.selected = [elCurrent.key]
          }
          return
        }
      }
      elCurrent = elPrev
    }
    this.selected = [elCurrent.key]
  } // selectPrevious

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * toggles given item selection
  * @param {string} key - item to be de/selected
  * @param {boolean} toggle - if true, item will be selected, if false, it will be deselected, if null or undefined it will be toggled
  */
  toggleSelected(key, toggle) {
    if(_.isNil(key)) return false
    key = _.toString(key)
    const items = this.selected
    const idx = items.indexOf(key)
    const new_status = _.isNil(toggle) ? idx<0 : !!toggle
    if(new_status && !this.multi) {
      if(this.urlhash) {
        const owner = this.closest('.mele-modal,.mele-window')
        const listener_id = _.toString(owner ? owner.getAttribute('mod') || owner.getAttribute('modal') : '')
        if(listener_id) this.updateUrlHash('#'+listener_id+this.urlhash+key)
      }
      this.changeSelected(key, true, true)
    }else if(new_status && idx<0) {
      this.changeSelected(items.concat([key]), true, false)
    }else if(!new_status && idx>=0) {
      this.changeSelected(items.filter(item=>key!=item), true, false)
    }
  } // toggleSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * change selection as required
  * @param {array} keys - items to be selected
  * @param {boolean} toggle - if true, item will be selected, if false, it will be deselected, if null or undefined it will be toggled
  */
  changeSelected(selected, context_user_interaction, force_if_notchanged) {
    if(this.classList.contains('readonly')) return
    this.setProperty('selected', selected, context_user_interaction, force_if_notchanged)
  } // changeSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * toggles given item collapse
  * @param {string} key - item to be de/collapsed
  * @param {boolean} toggle - if true, item will be selected, if false, it will be deselected, if null or undefined it will be toggled
  */
  toggleCollapse(key, toggle) {
    if(_.isNil(key)) return false
    if(this.noncollapsible) return false // don't allow toggling in noncollapsible lists
    key = _.toString(key)
    const items = this.collapse
    const idx = items.indexOf(key)
    const elItem = this._body.find('>:not([key=":comber"])@{'+key+'}')
    if(elItem && elItem.disabled) return // dont' allow toggling of disabled items
    const new_status = _.isNil(toggle) ? idx<0 : !!toggle
    if(new_status && idx<0) {
      this.collapse = items.concat([key]) // we cannot simply do .push() as this would change old value
    }else if(!new_status && idx>=0) {
      this.collapse = items.filter(item=>key!=item)
    }
    this.scrollToSelected()
  } // toggleCollapse

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * re-filter items
  * set selected,collapsed and hidden attributes on each item according to 'filter', 'callapsed', 'selected', 'show' and 'readonly' attributes of the list
  */
  _filterItems() {
    // build matching function
    let conditions = []

    // skip filtering/searching if data are provided externally
    if(typeof this.eventSearch !== 'function') {
      // conditions for filter
      _.toArray(this.filter).forEach(filter=>{
        if(!Array.isArray(filter)) return
        const prop = _.toString(filter[0]).replace(/[^\w\.]/g, '')
        const regexp = _.toString(filter[1])
        if(prop!='' && regexp!='') {
          conditions.push((new RegExp(regexp).toString())+'.test(item.'+prop+')')
        }
      })

      // conditions for searching
      const search = _.toLower(_.toString(this.search).replace(/['"]/g, ''))
      if(search!='') {
        let searchfields = _.toArray(this.searchfields).filter(prop=>{ return /^\w+\$?$/.test(prop) })
        if(searchfields.length==0) { searchfields = ['label$', 'label2$', 'value$'] }
        const search_conditions = []
        searchfields.forEach(searchfield=>{
          const m = searchfield.match(/^(\w+)(\$?)$/)
          if(!m) return
          if(m[2]=='$') {
            search_conditions.push('_.toLower(item.translate(_.toString(item.'+m[1]+'))).indexOf("'+search+'")>=0')
          }else {
            search_conditions.push('_.toLower(_.toString(item.'+m[1]+')).indexOf("'+search+'")>=0')
          }
        })
        if(search_conditions.length>0) { conditions.push(search_conditions.join(' || ')) }
      }
    }

    // conditions for collapsing
    const collapse = {}
    _.toArray(this.collapse).forEach(key=>{
      if(/^[^\'\"']+$/.test(key)) {
        conditions.push('key.substr(0,key.length-1).indexOf("'+key+'")!=0')
        collapse[key]=1
      }
    })

    // condition for show='selected'
    if(this.show=='selected' || this.readonly) {
      conditions.push('item.selected')
    }else if(this.show=='notselected') {
      conditions.push('!item.selected')
    }

    conditions = conditions.map(condition=>{ return '('+condition+')' }).join(' && ')
    const fcnVisible = new Function('me', 'item', conditions=='' ? 'return true' : 'const key=_.toString(item.key); if(key==":comber") return true; return('+conditions+')')

    // create helper hash with selected items; for single select, use only first item
    const selected = {}
    if(this.multi) {
      this.selected.forEach(key=>{ selected[key]=1 })
    }else {
      this.selected.slice(0, 1).forEach(key=>{ selected[key]=1 })
    }

    this._body.forAll('>:not([key=":comber"])', el=>{
      el.selected = !!selected[el.key]
      el.collapsed = !!collapse[el.key]
      el.hidden = !fcnVisible(this, el)
    })
    this.scrollToSelected()
  } // _filterItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * reorder items according to 'sort' attribute
  */
  _sortItems() {
    // skip sorting if data are provided externally
    if(typeof this.eventSearch === 'function') return

    const sort_conditions = []

    const sortfields = _.toArray(this.sort)
    if(sortfields.length===0) {
      this._body.forAll('>:not([key=":comber"])', el=>{
        el.clicked = (item, icon)=>{ this.itemClicked(item, icon) } // setup onclick handler
      })
      return
    }

    sortfields.forEach(sortfield=>{
      const m = sortfield.match(/^(\w+(?:\.\w+)*)([\$\#]?)([\+\-])$/)
      if(!m) return
      const [, key, format, order] = m
      const a = order==='+' ? 'a' : 'b'
      const b = order==='+' ? 'b' : 'a'
      if(format==='#') {
        // numerical sort
        sort_conditions.push(`(${a}.${key}-${b}.${key})`)
      }else if(format==='$') {
        // localized text sort
        sort_conditions.push(`${a}.translate(${a}.${key}).localeCompare(${b}.translate(${b}.${key}))`)
      }else {
        // normal textual sort
        sort_conditions.push(`_.toString(${a}.${key}).localeCompare(_.toString(${b}.${key}))`)
      }
    })
    sort_conditions.push('(a._uuid-b._uuid)||0')

    // build sort function
    const fcnSorting = new Function('a', 'b', 'return('+sort_conditions.join('||')+')')

    // build children list for each parent
    const children = { '': [] }
    this._body.forAll('>:not([key=":comber"])', el=>{
      const key = _.toString(el.key)
      const parent = key.replace(/[^>]*>?$/, '')
      if(!children[parent]) children[parent] = []
      children[parent].push(el)
    })

    // sort all children list
    for(const parent in children) {
      children[parent] = children[parent].sort(fcnSorting)
    }

    const getSubTree = function(el) {
      const key = _.toString(el.key)
      const childs = key=='' ? [] : (children[key] || [])
      let items = [el]
      childs.forEach(child=>{ items=items.concat(getSubTree(child)) })
      return items
    }

    // build flat list from children
    let sorted = []
    children[''].forEach(child=>{ sorted=sorted.concat(getSubTree(child)) })

    // move comber to the last place
    if(this._comber && (this._comber.nextElementSibling || this._comber.parentElement!==this._body)) {
      this._body.appendChild(this._comber)
    }

    // finally reorder items in DOM
    sorted[sorted.length] = this._comber
    for(let i=sorted.length-2; i>=0; i--) {
      const el = sorted[i]
      const next = sorted[i+1]
      const current_next = el.nextElementSibling
      if(next!==current_next) {
        try{
          this._body.insertBefore(el, next)
        }catch(e) {
          // ignore
        }
      }
    }

    this.scrollToSelected()
  } // _sortItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * remove items above the limit
  */
  _limitItems() {
    if(!this._body) return
    const limit = parseInt(this.limit)
    if(!limit) return
    const abslimit = Math.abs(limit)
    const itemElements = this.getItemElements()
    const remove = itemElements.length - abslimit
    if(remove<=0) return

    const start_idx = limit<0 ? 0 : abslimit // remove from beginning or end
    const end_idx = start_idx + remove
    for(let i=start_idx; i<end_idx; i++) {
      if(!itemElements[i].selected) itemElements[i].deleteElement()
    }

    this.scrollToSelected()
  } // _limitItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * updates which system buttons shall be displayed
  */
  _updateEditButton() {
    if(this.readonly || this.disabled) {
      this._editbutton.key = ''
      this._editbutton.iconleft = ''
    }else if(!this.multi && this.direction!='row' && this.show=='selected') {
      // dropdown
      this._editbutton.key = 'dropdown'
      this._editbutton.iconleft = 'arrow.s'
    }else if(!this.multi && this.direction=='row' && this.show=='selected' && this.editable) {
      // droodown with custom add
      this._editbutton.key = 'dropright'
      this._editbutton.iconleft = 'arrow.s'
    }else if(!this.multi && !this.editable) {
      this._editbutton.key = ''
      this._editbutton.iconleft = ''
    }else if(this.multi && (this.show=='all'||!this.show) && !this.editable) {
      this._editbutton.key = ''
      this._editbutton.iconleft = ''
    }else if(this.multi && this.show=='notselected') {
      this._editbutton.key = ''
      this._editbutton.iconleft = ''
    }else if(this.show=='all'||!this.show) {
      this._editbutton.key = ''
      this._editbutton.iconleft = ''
    }else if(this.direction=='row') {
      this._editbutton.key = 'listright'
      this._editbutton.iconleft = 'u.add'
    }else{
      this._editbutton.key = 'listdown'
      this._editbutton.iconleft = 'arrow.s'
    }
  } // _updateEditButton

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of items
  */
  coerceItem(json) {
    if(typeof (json)==='string' || typeof (json)==='number') {
      // string definition is a shortcut for {key: json}
      json = { key: _.toString(json) }
    }
    if(_.isObjectLike(json)) {
      json = _.extend({
        tag: 'mele-item',
        prefix: this.prefix,
        parentElementName: 'list',
        label: _.toString(this.label),
        label2: _.toString(this.label2),
        value: _.toString(this.value),
        attachments: _.toString(this.attachments),
        tooltip: _.toString(this.tooltip),
        iconleft: _.toString(this.iconleft),
        iconright: _.toString(this.iconright),
        indenticon: _.toString(this.indenticon),
        color: _.toString(this.color),
      }, json)
      return json
    }else{
      return null
    }
  }

  getItemElements(keys) {
    if(!this._body) return []
    if(_.isNil(keys)) {
      return this._body.findAll('>:not([key=":comber"])')
    }
    if(typeof (keys)==='string' || typeof (keys)==='number') { keys = [keys] }
    if(!Array.isArray(keys)) return []
    return keys.map(key=>{
      if(_.isNil(key)) return null
      const el = this._body.find('>@{'+_.toString(key)+'}:not([key=":comber"])')
      return el || null
    })
  } // getItemElements

  setItems(items) {
    this.deleteItems()
    this.appendItems(items)
    if(this.blank) { this.updateItems({ key: '' }) }
  }

  deleteItems(items) {
    if(!this._body) return
    if(_.isNil(items)) { return this._body.forAll('>:not([key=":comber"])', el=>el.deleteElement()) }

    items = Array.isArray(items) ? items : [items]
    items.forEach(item=>{
      if(typeof (item)==='string' || typeof (item)==='number') { item = { key: _.toString(item) } }
      if(_.isObjectLike(item) && typeof (item.key)!=='undefined') {
        this._body.forAll('>@{'+_.toString(item.key)+'}', el=>el.deleteElement())
        this.selected = this.selected.filter(sel=>sel!=item.key)
      }
    })
    if(this.selected.length==0 && !this.multi) {
      const first = this._body.find('>:not([key=":comber"])')
      if(first) { this.selected = [first.key] }
    }
    this.formStatusChanged()
    this.scrollToSelected()
  }

  prependItems(items, before_item) {
    const result = super.prependItems(items, before_item)

    // handle clicks on added items
    items = _.isNil(result) ? [] : Array.isArray(result) ? result : [result]
    items.forEach(el=>{
      el.clicked = (item, icon)=>{ this.itemClicked(item, icon) } // setup onclick handler
    })

    this._filterItems()
    this._sortItems()
    this._limitItems()
    return result
  }

  appendItems(items, after_item) {
    const result = super.appendItems(items, after_item)

    // handle clicks on added items
    items = _.isNil(result) ? [] : Array.isArray(result) ? result : [result]
    items.forEach(el=>{
      el.clicked = (item, icon)=>{ this.itemClicked(item, icon) } // setup onclick handler
    })

    this._filterItems()
    this._sortItems()
    this._limitItems()

    if(this.autoselect && items.length===1) {
      // when autoselect is turned on, select appended item
      // but only if we are in a module that is currently visible
      const module = this.closest('.mele-modal,.mele-window')
      if(module && module.focused) {
        this.selected = [items[0].key]
      }
    }
    return result
  }

  updateItems(items) {
    const new_item_candidates = !this.autoselect ? [] : (Array.isArray(items) ? items.map(i=>i?.key) : [items?.key]).filter(i=>i)
    const new_item_keys = new_item_candidates.filter(key=>!this.getItemElement(key))

    const result = super.updateItems(items)

    // handle clicks on added items
    items = _.isNil(result) ? [] : Array.isArray(result) ? result : [result]
    items.forEach((el, idx)=>{
      el.clicked = (item, icon)=>{ this.itemClicked(item, icon) } // setup onclick handler
      if(this.sortable) {
        el.addEventListener('dragstart', this._setOnDragstartHandler, false)
        el.setAttribute('draggable', true)
        el.sorting = parseInt(el.data.sorting) || (idx+1) * 1000
      }
    })

    this._filterItems()
    this._sortItems()
    this._limitItems()

    if(this.autoselect && items.length===1 && new_item_keys.length===1) {
      // when autoselect is turned on, select appended item
      // but only if we are in a module that is currently visible
      const module = this.closest('.mele-modal,.mele-window')
      if(module && module.focused) {
        this.selected = [new_item_keys[0]]
      }
    }
    return result
  }

  changeItemKey(old_key, new_key) {
    super.changeItemKey(old_key, new_key)
    this._filterItems()
    this._sortItems()
    this._limitItems()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Update from change records
  */
  update(params, key_parameter) {
    let list = params.list
    if(!Array.isArray(list)) return
    const full = !!params.full

    if(full) {
      this._body.forAll('>*', el=>el.deleteElement())
    }

    // create mele-item definitions
    list = list.map(item=>{
      const key = item[key_parameter || 'id']
      let list_item
      if(item.tag) {
        list_item = {
          tag: item.tag,
          key,
          data: _.omit(item, ['tag']),
        }
      }else{
        list_item = {
          key,
          data: item,
        }
      }
      return(list_item)
    })

    this.updateItems(list)
  } // update

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "prefix"
  * Changes prefix attribute for all items
  */
  prefixChanged(value, old) {
    this.invokeDependentChanges('prefix')
    value = _.toString(value)

    // change items generated automatically according to prefix
    if(this.auto) {
      const old_selected = this.selected
      this.deleteItems(this.getOptions(old))
      this.updateItems(this.getOptions(this.prefix))
      this.selected = old_selected
    }

    // update all items' prefix
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.prefix = value }) }

    // re-filter and re-sort
    this._filterItems()
    this._sortItems()
    this._limitItems()
  } // prefixChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "prefix"
  * Changes prefix attribute for all items
  */
  hiddenChanged(value, old) {
    this.prefixChanged(this.prefix, this.prefix)
  } // hiddenChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "auto"
  */
  autoChanged(value) {
    const auto_items = this.getOptions(this.prefix)
    if(value) {
      if(this.blank && auto_items.indexOf('')<0) { auto_items.unshift('') }
      this.updateItems(auto_items)
    }else{
      this.deleteItems(auto_items||[])
    }
  } // autoChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "blank"
  */
  blankChanged(value) {
    const elBlank = this._body.find('>@{}')
    if(value) {
      if(!elBlank) { this.updateItems({ key: '' }) }
    }else{
      if(elBlank) { this.deleteItems('') }
    }
  } // blankChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "multi"
  */
  multiChanged(value) {
    // just refresh items' selected property
    // create helper hash with selected items; for single select, use only first item
    const selected = {}
    let removable = this.show=='selected'
    if(this.multi) {
      this.selected.forEach(key=>{ selected[key]=1 })
    }else{
      this.selected.slice(0, 1).forEach(key=>{ selected[key]=1 })
      removable = false
    }

    this._body.forAll('>:not([key=":comber"])', el=>{
      el.selected = !!selected[el.key]
      el.removable = removable && selected[el.key] // set removable flag for items if it is multichoice showing selected only and the item is selected
    })

    this._updateEditButton()
  } // multiChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "direction"
  */
  directionChanged(value) {
    this._updateEditButton()
  } // directionChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "selected"
  */
  selectedChanged(value, old, context_user_interaction) {
    this.invokeDependentChanges('selected')
    this.appendSelectedCustomOptions()
    this._filterItems()
    this.eventSelected(this, value, old, context_user_interaction)
    this._updateEditButton()
    this.scrollToSelected()
  } // selectedChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "collapsed"
  */
  collapseChanged(value) {
    // change of collapsed items affects visibility of items, so we need to re-filter
    this._filterItems()
  } // collapseChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "show"
  */
  showChanged(value) {
    // show affects visibility of items, so we need to re-filter
    this._filterItems()
    this._updateEditButton()

    if(this.multi) { this.multiChanged(true, true) } // to hide/show removable flag from items
  } // showChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "readonly"
  */
  readonlyChanged(value, old) {
    if(this.show!='selected') {
      // readonly means displaying only selected items; however if we already display only selected items, there is no need for re-filter
      this._filterItems()
    }
    this._updateEditButton()
  } // readonlyChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "disabled"
  */
  disabledChanged(value, old) {
    this._updateEditButton()
  } // disabledChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "editable"
  */
  editableChanged(value, old) {
    this._updateEditButton()
    this.appendSelectedCustomOptions()
  } // editableChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle changes propagated to items
  */
  labelChanged(value) {
    value = _.toString(value)
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.label = _.toString(value) }) }
  }

  label2Changed(value) {
    value = _.toString(value)
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.label2 = _.toString(value) }) }
  }

  valueChanged(value) {
    value = _.toString(value)
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.value = _.toString(value) }) }
  }

  attachmentsChanged(value) {
    value = _.toString(value)
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.attachments = _.toString(value) }) }
  }

  colorChanged(value) {
    value = _.toString(value)
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.color = _.toString(value) }) }
  }

  tooltipChanged(value) {
    value = _.toString(value)
    this._body.forAll('>:not([key=":comber"])', el=>{ el.tooltip = value })
  }

  iconleftChanged(value) {
    value = _.toString(value)
    if(value!='') { this._body.forAll('>:not([key=":comber"])', el=>{ el.iconleft = _.toString(value) }) }
  }

  iconrightChanged(value, old, user_context) {
    value = _.toString(value)
    if(value!='') {
      this._body.forAll('>:not([key=":comber"])', el=>{ user_context ? el.iconrightChanged(value, value) : el.iconright = _.toString(value) })
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "sort"
  */
  sortChanged() {
    this._sortItems()
  } // sortChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "searchbar"
  */
  searchbarChanged(value) {
    this._search_wrapper.hidden = !value
    this._search_wrapper.forAll('mele-input', el=>{
      el.hidden = !value
    })
  } // searchbarChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "searchfields"
  */
  searchfieldsChanged(value) {
    this._filterItems()
  } // searchfieldsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "search"
  */
  searchChanged(value) {
    value = _.toString(value)
    this._search.value = value
    if(typeof this.eventSearch === 'function') {
      this.searching = true
      this.eventSearch(this, value)
    }else{
      this._filterItems()
    }
  } // searchChanged

  updateSearch(results) {
    const current_search = _.toString(this.search)
    if(!_.isObjectLike(results) || current_search != _.toString(results.search)) return // search field changed before the response came
    if(results) this.update(results)
    this.searching = false
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "filterbar"
  */
  filterbarChanged(value) {
    this._filters_wrapper.setAttribute(value?'':null)
  } // filterbarChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "filtersets"
  */
  filtersetsChanged(value) {
    // convert array of [setname,filterdefinition] to array of {label:setname, filter:filterdefinition}
    const items = _.toArray(value)
      .map(item=>{
        const tuple = _.toArray(item)
        return({
          label: _.toString(tuple[0]),
          filter: _.toString(tuple[1]),
        })
      })
      .filter(item=>{ return item.label!='' })
    this._filters.update(items, true)
    this._filterItems()
  } // filtersetsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "filterset"
  */
  filtersetChanged(value) {
    this._filters.selected = value // this will automatically set this.filter
  } // filtersetChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "filter"
  */
  filterChanged(value) {
    this._filterItems()
  } // filterChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "searching"
  */
  searchingChanged(value) {
    this._searching.hidden = !value
  } // searchingChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "prefix"
  * Changes prefix attribute for all items
  */
  autoselectableChanged(value, old) {
    if(value && !this._autoselectable) {
      this._autoselectable = this._header_wrapper.appendChildren({
        key: 'autoselectable',
        tag: 'mele-button',
        type: 'toolbar',
        iconleft: 'u.refresh',
      })

      this._autoselectable.eventButtonClicked = (btn)=>{
        btn.selected = !btn.selected
      }

      const orig = this._autoselectable.selectedChanged.bind(this._autoselectable)
      this._autoselectable.selectedChanged = () => {
        orig()
        const autoselect = !!this._autoselectable.selected
        if(this.autoselect!==autoselect) {
          this.autoselect = autoselect
        }
      }
    }else if(!value && this._autoselectable) {
      this._autoselectable.deleteElement()
      this._autoselectable = null
    }
  } // autoselectableChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "prefix"
  * Changes prefix attribute for all items
  */
  autoselectChanged(value, old) {
    value = !!value
    if(!this._autoselectable) return
    if(this._autoselectable.selected !== value) {
      this._autoselectable.selected = value
    }
  } // autoselectChanged

  bodyClicked() {
    if(this.show=='selected') { this.editbutton(this._editbutton.key) }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handle clicks on list items
  */
  itemClicked(item, icon) {
    if(this.disabled || this.readonly) return

    if(this.isJustIconCollapsible && icon && icon.includes('collapse')) {
      this.toggleCollapse(item.key)
      return
    }

    this.autoselect = false

    if(icon && icon.includes('remove')) {
      // click on remove icon should deselect the item
      this.toggleSelected(item.key, false)
      return
    }else if(icon && !icon.includes('collapse')) {
      this.eventIconClicked(this, item, icon)
      return
    }

    if(/>$/.test(item.key)) {
      // click on collapsible item
      if(!this.isJustIconCollapsible) { this.toggleCollapse(item.key) }
      if(this.allselectable) {
        this.toggleSelected(item.key, true)
      }
    }else if(this.show=='selected') {
      // only selected items are displayed
      if(this.multi) {
        // TODO: click on multi list
        // click on item when only selected are displayed does nothing
        // click on special + item will open dropdown with not yet selected items
      }else{
        // single-value
        if(this.direction=='row') {
          // direction=row & show=selected & !multi has switching behavior
          // click on the displayed selected value will select next one available
          const elNext = item.nextElementSibling || item.parentElement.firstChild
          if(elNext) { this.toggleSelected(_.toString(elNext.key), true) }
        }else{
          // direction=column & show=selected & !multi has dropdown behavior
          this.editbutton('dropdown')
        }
      }
    }else{
      // all or not-selected items are displayed
      if(this.multi) {
        // for multi-value - switch selected state of clicked item
        this.toggleSelected(item.key)
      }else{
        // for single-value select clicked item
        this.toggleSelected(item.key, true)
      }
    }
  }

  addCustomOption(key) {
    key = _.toString(key)
    if(key=='') return ''
    const existing = this.getItem(key)
    if(existing) return key
    this.prependItems({ key, label: key, prefix: '\u200B' })
    return key
  } // addCustomOption

  appendSelectedCustomOptions() {
    if(!this.editable) return
    this.selected.reverse().forEach(key=>{
      if(key!='' && !this.getItem(key)) {
        this.prependItems({ key, label: key, prefix: '\u200B' })
      }
    })
  } // appendSelectedCustomOptions

  eventIconClicked(self, item, icon) {
    this.publish('mmListIconClicked', { holder: this.key, item: _.toString(item.key), iconclass: icon })
    this.itemClicked(item)
  }

  eventSelected(self, items, old, context_user_interaction) {
    this.publish('mmListSelected', { holder: this.key, selected: items.join('∫'), old: old.join('∫'), interactive: !!context_user_interaction })
  }

  eventSorted(self, item, value) {
    this.publish('mmListSorted', { holder: this.key, key: _.toString(item.key), value })
  }

  editbutton(action) {
    const elModal=this.modalOpen({
      key: ':dropdown',
      position: this.searchbar && this.direction!='row' ? 'over' : 'below',
      outclick: ':modal.cancel',
      header: this.prompt,
      headerbuttons: ['modalx'],
      class: 'dropdown',
    })

    const me=this
    const phone = this.closest('.mele-modal, .mele-window').behavior=='phone'
    const multi = /^list/.test(action)
    let skipEvent = true
    let selector = {
      tag: 'mele-list',
      class: 'dropdown',
      show: multi ? 'notselected' : 'all',
      // searchbar: true,
      multi,
      eventSelected: function(self, newValue, oldValue, context_user_interaction) {
        if(skipEvent) {
          skipEvent=false
          if(!context_user_interaction) return
        }
        me._comber.value = ''
        if(phone) {
          // close modal list when clicked on single selector
          const selected = this.selected
          if(!multi) elModal.modalClose()
          me.changeSelected(selected, context_user_interaction)
        }else{
          const selected = this.selected
          if(!multi) elModal.modalClose() // close modal list when clicked on single selector
          me.changeSelected(selected, context_user_interaction)
          if(multi) { elModal.reposition() }
        }
        const parentInput = me.closest('mele-input')
        if(parentInput && parentInput.eventSearch) {
          // if it is lookup, we have to copy selected items to primary list
          const selected = me.selected
          me.setItems(this.items.filter(item=>selected.indexOf(_.toString(item.key))>=0))
        }
      },
    }

    selector = _.extend(
      _.pick(this, ['prefix', 'auto', 'items', 'label', 'label2', 'value', 'attachments', 'tooltip', 'color', 'iconleft', 'iconright', 'sort', 'filter', 'searchbar', 'editable', 'eventSearch']),
      selector,
    )

    const elSelector = elModal.appendItems(selector)
    if(this.searchbar) { elSelector.find('mele-header@search input').focus() }
    elSelector.selected = this.selected
  }
} // class meleList

meleList._properties = PROPERTIES
meleList.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-list', meleList)
